import React, { useEffect, useState } from "react";
import { Image, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import FileUploader from "../components/FileUploader";

export default function AdminGallery() {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/gallery");
            setImages(data);
        } catch {
            setImages([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const onUploaded = async (data) => {
        toast.success("Gallery image uploaded");
        await load();
    };

    const removeImage = async (image) => {
        if (!window.confirm("Delete this gallery image?")) return;
        try {
            await api.delete(`/gallery/${encodeURIComponent(image.id)}`);
            toast.success("Gallery image removed");
            await load();
        } catch {
            toast.error("Could not delete image");
        }
    };

    return (
        <div data-testid="admin-gallery">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="font-serif-display text-2xl md:text-3xl">Gallery</h1>
                    <p className="text-sm text-slate-500 mt-2">Upload and manage images shown on the public gallery page.</p>
                </div>
                <div className="w-full max-w-sm">
                    <FileUploader
                        kind="image"
                        folder="gallery"
                        label="Upload new gallery image"
                        onUploaded={onUploaded}
                        testidPrefix="admin-gallery-upload"
                    />
                </div>
            </div>

            {loading ? (
                <div className="font-mono-cap text-xs text-slate-500">Loading…</div>
            ) : images.length === 0 ? (
                <div className="text-slate-600">No gallery images yet. Upload one to get started.</div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {images.map((image) => (
                        <div key={image.id} className="group overflow-hidden rounded-sm border border-slate-900/10 bg-white shadow-sm">
                            <div className="aspect-[4/3] bg-slate-100">
                                <img src={image.public_url} alt={image.original_filename || "Gallery image"} className="w-full h-full object-cover" />
                            </div>
                            <div className="p-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <div className="font-serif-display text-lg text-slate-900">{image.original_filename || "Untitled"}</div>
                                        <div className="text-xs text-slate-500 mt-2">Uploaded {new Date(image.created_at).toLocaleDateString()}</div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => removeImage(image)}
                                        className="inline-flex items-center gap-2 text-red-700 text-xs font-medium"
                                        data-testid={`admin-gallery-delete-${image.id}`}
                                    >
                                        <Trash2 className="w-4 h-4" /> Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
